import * as XLSX from 'xlsx';
import { formatBusinessCategoryLabel } from '@/lib/business-category';
import { sortBusinessDirectoryEntries } from '@/lib/business-directory-params';
import { formatDisplayPhone } from '@/lib/phone-utils';
import { triggerBlobFileDownload } from '@/lib/trigger-download';
import type { BusinessDirectorySort, CommunityBusiness } from '@/types';

function sanitizeFilenamePart(value: string): string {
  return value
    .replace(/[/\\?%*:|"<>]/g, '')
    .replace(/\s+/g, '_')
    .trim()
    .slice(0, 80);
}

function buildDirectoryRows(
  businesses: CommunityBusiness[],
  sort: BusinessDirectorySort
): Record<string, string>[] {
  const ordered = sortBusinessDirectoryEntries(businesses, sort);

  return ordered.map((business) => {
    const ownerName = business.owner.name?.trim() || '';
    const businessName = business.name.trim();
    const category =
      formatBusinessCategoryLabel(
        business.category,
        business.subCategory,
        business.selfDefinedCategory
      ) || '';
    const phone = business.phone ? formatDisplayPhone(business.phone) : '';
    const website = business.website?.trim() || '';

    if (sort === 'name') {
      return {
        Name: ownerName || businessName,
        'Business Name': businessName,
        Category: category,
        Phone: phone,
        Website: website,
      };
    }

    return {
      'Business Name': businessName,
      Name: ownerName,
      Category: category,
      Phone: phone,
      Website: website,
    };
  });
}

export function downloadBusinessDirectoryExcel(
  businesses: CommunityBusiness[],
  communityName: string,
  sort: BusinessDirectorySort = 'business'
): void {
  const rows = buildDirectoryRows(businesses, sort);
  const worksheet = XLSX.utils.json_to_sheet(rows);
  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, worksheet, 'Business Directory');

  const arrayBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' }) as ArrayBuffer;
  const blob = new Blob([arrayBuffer], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  });
  const filename = `${sanitizeFilenamePart(communityName) || 'community'}_business_directory.xlsx`;
  triggerBlobFileDownload(blob, filename);
}
